<template>
  <oxd-table-filter :filter-title="$t('payroll.payroll_deductions')">
    <oxd-form @submit-valid="filterItems">
      <oxd-form-row>
        <oxd-grid :cols="4" class="orangehrm-full-width-grid">
          <oxd-grid-item>
            <employee-autocomplete
              v-model="filters.employee"
              :rules="rules.employee"
              :params="{
                includeEmployees: 'currentAndPast',
              }"
            />
          </oxd-grid-item>
          <oxd-grid-item>
            <date-input
              v-model="filters.effectiveFrom"
              :rules="rules.effectiveFrom"
              :label="$t('payroll.effective_from')"
            />
          </oxd-grid-item>
          <oxd-grid-item>
            <date-input
              v-model="filters.effectiveTo"
              :rules="rules.effectiveTo"
              :label="$t('payroll.effective_to')"
            />
          </oxd-grid-item>
          <oxd-grid-item>
            <oxd-input-field
              v-model="filters.deductionType"
              type="select"
              :rules="rules.type"
              :options="typeOptions"
              :label="$t('payroll.deduction_type')"
            />
          </oxd-grid-item>
        </oxd-grid>
      </oxd-form-row>

      <oxd-divider />

      <oxd-form-actions>
        <oxd-button
          display-type="ghost"
          :label="$t('general.reset')"
          type="reset"
        />
        <oxd-button
          class="orangehrm-left-space"
          display-type="secondary"
          :label="$t('general.search')"
          type="submit"
        />
      </oxd-form-actions>
    </oxd-form>
  </oxd-table-filter>
  <br />
  <div class="orangehrm-paper-container">
    <div class="orangehrm-header-container">
      <div>
        <oxd-button
          :label="$t('general.add')"
          icon-name="plus"
          display-type="secondary"
          @click="onClickAdd"
        />
      </div>
    </div>
    <table-header
      :total="total"
      :loading="isLoading"
      :selected="checkedItems.length"
      @delete="onClickDeleteSelected"
    ></table-header>
    <div class="orangehrm-container">
      <oxd-card-table
        v-model:selected="checkedItems"
        :headers="headers"
        :items="items?.data"
        :selectable="true"
        :clickable="false"
        :loading="isLoading"
        row-decorator="oxd-table-decorator-card"
      />
    </div>
    <div class="orangehrm-bottom-container">
      <oxd-pagination
        v-if="showPaginator"
        v-model:current="currentPage"
        :length="pages"
      />
    </div>
    <delete-confirmation ref="deleteDialog"></delete-confirmation>
  </div>
</template>

<script>
import {formatEmployeeFullName} from '@/core/util/helper/employeeDisplayName';
import {computed, ref} from 'vue';
import {navigate} from '@/core/util/helper/navigation';
import {APIService} from '@/core/util/services/api.service';
import usePaginate from '@ohrm/core/util/composable/usePaginate';
import useDateFormat from '@/core/util/composable/useDateFormat';
import {validDateFormat, validSelection} from '@/core/util/validation/rules';
import DeleteConfirmationDialog from '@ohrm/components/dialogs/DeleteConfirmationDialog';
import EmployeeAutocomplete from '@/core/components/inputs/EmployeeAutocomplete.vue';

export default {
  components: {
    'employee-autocomplete': EmployeeAutocomplete,
    'delete-confirmation': DeleteConfirmationDialog,
  },

  setup() {
    const {userDateFormat} = useDateFormat();

    const rules = {
      effectiveFrom: [validDateFormat(userDateFormat)],
      effectiveTo: [validDateFormat(userDateFormat)],
      employee: [validSelection],
      deductionType: [validSelection],
    };
    const filters = ref({
      effectiveFrom: null,
      effectiveTo: null,
      employee: null,
      deductionType: null,
    });

    const serializedFilters = computed(() => {
      return {
        effectiveTo: filters.value.effectiveTo,
        effectiveFrom: filters.value.effectiveFrom,
        empNumber: filters.value.employee?.id,
        deductionType: filters.value?.deductionType?.param,
      };
    });

    const http = new APIService(
      window.appGlobal.baseUrl,
      '/api/v2/payroll/deductions',
    );

    const formatByCurrency = (value, currency, showDecimals = false) => {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency,
        minimumFractionDigits: showDecimals ? 2 : 0,
        maximumFractionDigits: showDecimals ? 2 : 0,
      }).format(value);
    };

    const attendanceRecordNormalizer = (data) => {
      return data.map((item) => {
        return {
          id: item.id,
          employee: formatEmployeeFullName(item.employee),
          deductionName: item.deductionName,
          deductionType: item.deductionType,
          amount: `${formatByCurrency(item.amount, item.currency.id)}`,
          currency: item.currency.name,
          timePeriod: `${item.effectiveFrom} - ${item.effectiveTo}`,
        };
      });
    };

    const {
      total,
      pages,
      pageSize,
      response,
      isLoading,
      execQuery,
      currentPage,
      showPaginator,
    } = usePaginate(http, {
      query: serializedFilters,
      normalizer: attendanceRecordNormalizer,
    });
    return {
      http,
      rules,
      total,
      pages,
      filters,
      pageSize,
      isLoading,
      execQuery,
      currentPage,
      showPaginator,
      items: response,
    };
  },

  data() {
    return {
      headers: [
        {
          name: 'employee',
          slot: 'title',
          title: this.$t('general.employee_name'),
          style: {flex: 1},
        },
        {
          name: 'deductionName',
          slot: 'title',
          title: this.$t('payroll.deduction_name'),
          style: {flex: 1},
        },
        {
          name: 'deductionType',
          slot: 'title',
          title: this.$t('payroll.deduction_type'),
          style: {flex: 1},
        },
        {
          name: 'amount',
          slot: 'title',
          title: this.$t('payroll.deduction_amount'),
          style: {flex: 1},
        },
        {
          name: 'currency',
          slot: 'title',
          title: this.$t('payroll.currency_id'),
          style: {flex: 1},
        },
        {
          name: 'timePeriod',
          slot: 'title',
          title: this.$t('payroll.time_period'),
          style: {flex: 1},
        },
        {
          name: 'actions',
          slot: 'action',
          title: this.$t('general.actions'),
          style: {flex: 1},
          cellType: 'oxd-table-cell-actions',
          cellRenderer: this.actionRenderer,
        },
      ],
      checkedItems: [],
      typeOptions: [
        {
          id: 1,
          param: 'tax',
          label: this.$t('payroll.tax'),
        },
        {
          id: 2,
          param: 'insurance',
          label: this.$t('payroll.insurance'),
        },
        {
          id: 3,
          param: 'loan',
          label: this.$t('payroll.loan'),
        },
        {
          id: 4,
          param: 'advance',
          label: this.$t('payroll.advance'),
        },
        {
          id: 5,
          param: 'other',
          label: this.$t('payroll.other'),
        },
      ],
    };
  },

  methods: {
    onClickAdd() {
      navigate('/payroll/addPayrollDeductions');
    },
    actionRenderer(...[, , , row]) {
      const cellConfig = {};
      cellConfig.edit = {
        onClick: () => this.onClickEdit(row),
        props: {
          name: 'pencil-fill',
        },
      };
      cellConfig.delete = {
        onClick: () => this.onClickDelete(row),
        component: 'oxd-icon-button',
        props: {
          name: 'trash',
        },
      };
      return {
        props: {
          header: {
            cellConfig,
          },
        },
      };
    },
    onClickEdit(item) {
      navigate('/payroll/editPayrollDeductions/{id}', {id: item.id});
    },
    onClickDeleteSelected() {
      const ids = this.checkedItems.map((index) => {
        return this.items?.data[index].id;
      });
      this.$refs.deleteDialog.showDialog().then((confirmation) => {
        if (confirmation === 'ok') {
          this.deleteItems(ids);
        }
      });
    },
    onClickDelete(item) {
      this.$refs.deleteDialog.showDialog().then((confirmation) => {
        if (confirmation === 'ok') {
          this.deleteItems([item.id]);
        }
      });
    },
    deleteItems(items) {
      if (items instanceof Array) {
        this.isLoading = true;
        this.http
          .deleteAll({
            ids: items,
          })
          .then(() => {
            return this.$toast.deleteSuccess();
          })
          .then(() => {
            this.isLoading = false;
            this.resetDataTable();
          });
      }
    },
    async resetDataTable() {
      this.checkedItems = [];
      await this.execQuery();
    },
    async filterItems() {
      await this.execQuery();
    },
  },
};
</script>
